import {useEffect, useRef} from 'react';
import {
    YANDEX_NDD_WIDGET_SCRIPT,
    yandexNddDefaultCity,
    yandexNddStationId,
    yandexNddWeightG,
} from '../../util/yandexNddConfig';

function loadWidgetScript() {
    return new Promise((resolve, reject) => {
        if (typeof window === 'undefined') {
            reject(new Error('no window'));
            return;
        }
        if (window.YaDelivery) {
            resolve();
            return;
        }
        const existing = document.querySelector(`script[src="${YANDEX_NDD_WIDGET_SCRIPT}"]`);
        if (existing) {
            if (window.YaDelivery) {
                resolve();
                return;
            }
            existing.addEventListener('load', () => resolve());
            existing.addEventListener('error', () => reject(new Error('widget script')));
            return;
        }
        const s = document.createElement('script');
        s.async = true;
        s.src = YANDEX_NDD_WIDGET_SCRIPT;
        s.onload = () => resolve();
        s.onerror = () => reject(new Error('widget script'));
        document.head.appendChild(s);
    });
}

function parseRubValue(v) {
    if (typeof v === 'number' && !Number.isNaN(v)) {
        return v;
    }
    if (typeof v === 'string') {
        const n = parseFloat(String(v).replace(/[^\d.,-]/g, '').replace(',', '.'));
        if (!Number.isNaN(n)) {
            return n;
        }
    }
    return null;
}

/**
 * Виджет ndd-widget.landpro в CustomEvent кладёт только id + address; сумма не в detail.
 * После расчёта оффера скрипт кладёт её в window.YaDelivery.pointOfferMap[id].pricing_total.
 */
function feeFromYaDeliveryOfferMap(pointId) {
    if (!pointId || typeof window === 'undefined') {
        return null;
    }
    const entry = window.YaDelivery?.pointOfferMap?.[pointId];
    if (!entry || entry.error) {
        return null;
    }
    return parseRubValue(entry.pricing_total);
}

function extractFee(detail) {
    if (!detail || typeof detail !== 'object') {
        return null;
    }
    const cands = [
        detail.delivery_price,
        detail.price,
        detail.cost,
        detail.pricing_total,
        detail?.offer_details?.pricing_total,
        detail?.tariff?.price,
        detail?.delivery?.price,
    ];
    for (const p of cands) {
        const n = parseRubValue(p);
        if (n != null) {
            return n;
        }
    }
    return null;
}

/**
 * Виджет выбора ПВЗ / постамата (Яндекс НДД).
 * @param {{ active: boolean, city?: string, sourcePlatformStation?: string, onSelectionChange: (p: { pointId: string, addressLine: string, feeRub: number|null }) => void }} props
 *        city — в виджете Landpro задаёт начальную строку поиска (можно полный адрес), см. defaultCity + geocode.
 */
const YandexPvzWidget = ({active, city, sourcePlatformStation, onSelectionChange}) => {
    const containerIdRef = useRef(`ya-pvz-${Math.random().toString(36).slice(2, 12)}`);
    const containerId = containerIdRef.current;
    const onSelRef = useRef(onSelectionChange);
    onSelRef.current = onSelectionChange;

    const stationId = (sourcePlatformStation && String(sourcePlatformStation).trim()) || yandexNddStationId;

    useEffect(() => {
        if (!active || !stationId) {
            return undefined;
        }

        let cancelled = false;
        const cancelledTimers = [];

        const startWidget = () => {
            if (cancelled || typeof window === 'undefined' || !window.YaDelivery) {
                return;
            }
            try {
                window.YaDelivery.createWidget({
                    containerId,
                    params: {
                        city: city || yandexNddDefaultCity,
                        size: {
                            height: '450px',
                            width: '100%',
                        },
                        source_platform_station: stationId,
                        physical_dims_weight_gross: yandexNddWeightG,
                        delivery_price: (price) =>
                            price != null && price !== '' ? `${price} руб` : '',
                        delivery_term: 1,
                        show_select_button: true,
                        filter: {
                            type: ['pickup_point', 'terminal'],
                            is_yandex_branded: false,
                            payment_methods: ['already_paid', 'card_on_receipt'],
                            payment_methods_filter: 'or',
                        },
                    },
                });
            } catch (e) {
                console.error('YaDelivery.createWidget', e);
            }
        };

        const onPoint = (ev) => {
            const detail = ev.detail;
            const addr = detail?.address;
            const full = addr?.full_address || '';
            const addressLine =
                full ||
                [addr?.locality, addr?.street, addr?.house].filter(Boolean).join(', ');
            const pointId = detail?.id != null ? String(detail.id) : '';
            const emit = (feeRub) => {
                onSelRef.current?.({
                    pointId,
                    addressLine: addressLine || '',
                    feeRub,
                });
            };
            const fromDetail = extractFee(detail);
            const fromMap = feeFromYaDeliveryOfferMap(pointId);
            const feeRub = fromDetail != null ? fromDetail : fromMap;
            emit(feeRub);
            if (feeRub == null && pointId) {
                const t = window.setTimeout(() => {
                    if (cancelled) {
                        return;
                    }
                    const late = feeFromYaDeliveryOfferMap(pointId);
                    if (late != null) {
                        emit(late);
                    }
                }, 400);
                cancelledTimers.push(t);
            }
        };

        const onScriptReady = () => {
            if (!cancelled) {
                startWidget();
            }
        };

        loadWidgetScript()
            .then(() => {
                if (cancelled) {
                    return;
                }
                if (window.YaDelivery) {
                    startWidget();
                } else {
                    document.addEventListener('YaNddWidgetLoad', onScriptReady);
                }
            })
            .catch(() => {});

        document.addEventListener('YaNddWidgetPointSelected', onPoint);

        return () => {
            cancelled = true;
            cancelledTimers.forEach((t) => window.clearTimeout(t));
            document.removeEventListener('YaNddWidgetPointSelected', onPoint);
            document.removeEventListener('YaNddWidgetLoad', onScriptReady);
            const el = document.getElementById(containerId);
            if (el) {
                el.innerHTML = '';
            }
        };
    }, [active, city, containerId, stationId]);

    /**
     * setParams по доке НДД не поддерживает source_platform_station — передача ломает виджет.
     * Город меняем только здесь; станция отгрузки задаётся один раз в createWidget.
     */
    useEffect(() => {
        if (!active || typeof window === 'undefined' || !window.YaDelivery?.setParams) {
            return;
        }
        try {
            window.YaDelivery.setParams({
                city: city || yandexNddDefaultCity,
            });
        } catch {
            /* виджет может ещё не быть готов */
        }
    }, [active, city]);

    if (!active) {
        return null;
    }

    if (!stationId) {
        return (
            <p className="text-sm text-amber-800">
                Не задана станция отгрузки: включите API на бэкенде (yandex.b2b) или REACT_APP_YANDEX_NDD_STATION_ID.
            </p>
        );
    }

    return (
        <div
            id={containerId}
            className="min-h-[450px] w-full overflow-hidden rounded-2xl border border-stone-200 bg-stone-50"
        />
    );
};

export default YandexPvzWidget;
